// EditEventForm.js
import { useState, useEffect, useCallback } from "react";
import { useUpdateEventMutation, useDeleteEventMutation, useAddNewEventMutation } from "./eventsApiSlice"; // <-- Add useAddNewEventMutation
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import useAuth from "../../hooks/useAuth";
import dayjs from 'dayjs';

const EditEventForm = ({ event, users }) => { // 'event' prop might be undefined for new event
    const { isManager, isAdmin, id: currentUserId } = useAuth();
    const navigate = useNavigate();

    // Mutations for both update and add new
    const [updateEvent, { isLoading: isUpdating, isSuccess: isUpdateSuccess, isError: isUpdateError, error: updateError }] = useUpdateEventMutation();
    const [addNewEvent, { isLoading: isAdding, isSuccess: isAddSuccess, isError: isAddError, error: addError }] = useAddNewEventMutation(); // <-- Add new event mutation
    const [deleteEvent, { isLoading: isDeleting, isSuccess: isDelSuccess, isError: isDelError, error: delError }] = useDeleteEventMutation();

    const isEditMode = !!event?.id; // Check if we are in edit mode (event.id exists)
    const isLoading = isUpdating || isAdding; // Combined loading state for save operation
    const isSuccess = isUpdateSuccess || isAddSuccess; // Combined success state
    const isError = isUpdateError || isAddError; // Combined error state
    const error = updateError || addError; // Combined error object

    const [eventData, setEventData] = useState(() => {
        if (isEditMode) {
            const initialDate = event.date ? dayjs(event.date).format('YYYY-MM-DD') : '';
            const initialTime = event.time ? dayjs(`2000-01-01T${event.time}`).format('HH:mm') : '';
            return {
                id: event.id,
                name: event.name || '',
                date: initialDate,
                time: initialTime,
                duration: event.duration || '',
                organizer: event.organizer || '',
                place: event.place || '',
                category: event.category || '',
                eventType: event.eventType || 'Offline',
                zoomLink: event.zoomLink || '',
                googleMapUrl: event.googleMapUrl || '',
                errors: {}
            };
        }
        // For new event, initialize with empty values
        return {
            id: '', // New event will not have an ID yet
            name: '',
            date: '',
            time: '',
            duration: '',
            organizer: '',
            place: '',
            category: '',
            eventType: 'Offline',
            zoomLink: '',
            googleMapUrl:'',
            errors: {}
        };
    });

    const [selectedFile, setSelectedFile] = useState(null);
    const [currentBannerUrl, setCurrentBannerUrl] = useState(isEditMode ? (event?.banner?.url || null) : null);
    const [formError, setFormError] = useState('');

    // This useEffect will only run in edit mode to sync event prop changes
    useEffect(() => {
        if (isEditMode && event?.id && (event.id !== eventData.id || !eventData.name)) {
            const newDate = event.date ? dayjs(event.date).format('YYYY-MM-DD') : '';
            const newTime = event.time ? dayjs(`2000-01-01T${event.time}`).format('HH:mm') : '';
            setEventData({
                id: event.id,
                name: event.name || '',
                date: newDate,
                time: newTime,
                duration: event.duration || '',
                organizer: event.organizer || '',
                place: event.place || '',
                category: event.category || '',
                eventType: event.eventType || 'Offline',
                zoomLink: event.zoomLink || '',
                googleMapUrl: event.googleMapUrl || '',
                errors: {}
            });
            setCurrentBannerUrl(event.banner?.url || null);
        }
    }, [event, eventData.id, eventData.name, isEditMode]);

    useEffect(() => {
        if (isSuccess || isDelSuccess) {
            navigate('/dashboard/events');
        }
    }, [isSuccess, isDelSuccess, navigate]);

    useEffect(() => {
        if (isError) {
            setFormError(error?.data?.message || "इवेंट सेव करने में त्रुटि हुई।");
        } else if (isDelError) {
            setFormError(delError?.data?.message || "इवेंट डिलीट करने में त्रुटि हुई।");
        } else {
            setFormError('');
        }
    }, [isError, isDelError, error, delError]);

    const handleInputChange = useCallback((e) => {
        const { name, type, files, value } = e.target;

        if (!name) {
            console.warn("⚠️ Input field without a name!");
            return;
        }

        if (type === 'file') {
            const file = files?.[0];
            setSelectedFile(file);
            setCurrentBannerUrl(file ? URL.createObjectURL(file) : null); // For new file, always set URL from new blob
        } else {
            setEventData(prev => {
                const newState = {
                    ...prev,
                    [name]: value,
                    errors: {
                        ...prev.errors,
                        [name]: ''
                    }
                };
                return newState;
            });
        }
    }, []); // Removed event?.banner?.url from dependency since it's only for initial render

    const validate = useCallback(() => {
        const {
            name = '', organizer = '', date = '', time = '', place = '',
            category = '', eventType = '', zoomLink = '', googleMapUrl = ''
        } = eventData;

        const newErrors = {};

        if (!name.trim()) newErrors.name = 'इवेंट का नाम आवश्यक है।';
        if (!organizer.trim()) newErrors.organizer = 'आयोजक का नाम आवश्यक है।';
        if (!date) newErrors.date = 'इवेंट की तारीख आवश्यक है।';
        if (!time) newErrors.time = 'इवेंट का समय आवश्यक है।';
        if (!place.trim()) newErrors.place = 'इवेंट का स्थान आवश्यक है।';
        if (!category.trim()) newErrors.category = 'इवेंट की श्रेणी आवश्यक है।';
        if (!eventType.trim()) newErrors.eventType = 'इवेंट का प्रकार आवश्यक है।';
        if (!googleMapUrl.trim()) newErrors.googleMapUrl = 'मानचित्र लिंक आवश्यक है।';

        const needsZoom = eventType === 'Online' || eventType === 'Hybrid';
        if (category === 'Yearly') {
            if (!selectedFile && !currentBannerUrl) { // Check if either new file or existing URL is present
                newErrors.banner = 'वार्षिक इवेंट के लिए एक बैनर छवि आवश्यक है।';
            }
            if (needsZoom && !zoomLink.trim()) {
                newErrors.zoomLink = 'ऑनलाइन या हाइब्रिड वार्षिक इवेंट के लिए ज़ूम लिंक आवश्यक है।';
            }
        } else {
            if (needsZoom && !zoomLink.trim()) {
                newErrors.zoomLink = 'ऑनलाइन या हाइब्रिड इवेंट के लिए ज़ूम लिंक आवश्यक है।';
            }
        }
        
        return newErrors;
    }, [
        eventData.name, eventData.organizer, eventData.date, eventData.time,
        eventData.place, eventData.category, eventData.eventType, eventData.zoomLink,eventData.googleMapUrl,
        selectedFile, currentBannerUrl // These dependencies correctly trigger re-validation
    ]);

    useEffect(() => {
        const computedErrors = validate();
        setEventData(prev => ({ ...prev, errors: computedErrors }));
    }, [
        eventData.name, eventData.organizer, eventData.date, eventData.time,
        eventData.place, eventData.category, eventData.eventType, eventData.zoomLink,eventData.googleMapUrl,
        selectedFile, currentBannerUrl,
        validate
    ]);

    // आपका EditEventForm.js में handleSubmit फ़ंक्शन (जैसा आपने अभी दिया है, यह ठीक है)
const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading || isDeleting) return;

    const currentErrors = validate();
    if (Object.keys(currentErrors).length > 0) {
        setEventData(prev => ({ ...prev, errors: currentErrors }));
        setFormError("कृपया सभी आवश्यक फ़ील्ड्स को सही ढंग से भरें।");
        return;
    }
    setFormError('');

    const formData = new FormData();
    if (isEditMode) {
        formData.append('id', eventData.id);
    }

    formData.append('name', eventData.name);
    formData.append('date', eventData.date);
    formData.append('time', eventData.time);
    formData.append('organizer', eventData.organizer);
    formData.append('place', eventData.place);
    formData.append('category', eventData.category);
    formData.append('eventType', eventData.eventType);
    formData.append('duration', eventData.duration || '');
    formData.append('googleMapUrl', eventData.googleMapUrl || '');

    if (['Online', 'Hybrid'].includes(eventData.eventType)) {
        formData.append('zoomLink', eventData.zoomLink || '');
    } else {
        formData.append('zoomLink', '');
    }

    if (selectedFile) {
        formData.append('banner', selectedFile);
    } else if (currentBannerUrl && isEditMode) {
        formData.append('existingBannerUrl', currentBannerUrl);
    } else if (!selectedFile && !currentBannerUrl && isEditMode) {
        formData.append('removeBanner', 'true');
    } else {
        formData.append('banner', '');
    }

    console.log("--- FINAL FormData CHECK before dispatch (Frontend) ---");
    for (let pair of formData.entries()) {
        console.log(pair[0]+ ': ' + pair[1]);
    }
    console.log("--- END FormData CHECK (Frontend) ---");

    if (isEditMode) {
        await updateEvent(formData);
    } else {
        await addNewEvent(formData);
    }
};

    const handleDelete = async () => {
        if (window.confirm("क्या आप वाकई इस इवेंट को डिलीट करना चाहते हैं?")) {
            await deleteEvent({ id: event.id });
        }
    };

    const { errors } = eventData;
    const canSave = !isLoading && !isDeleting && Object.keys(errors).length === 0;

    // Determine heading text based on mode
    const headingText = isEditMode ? "इवेंट एडिट करें" : "नया इवेंट बनाएँ";
    const saveButtonText = isEditMode ? "सेव करें" : "इवेंट बनाएँ";

    return (
        <>
            <h2 className="pageHeading">{headingText}</h2>

            {(isError || isDelError || formError) && (
                <Alert variant="danger" className="errmsg">
                    {(formError || error?.data?.message || delError?.data?.message) ?? 'एक अनपेक्षित त्रुटि हुई।'}
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Group className='formGroup'>
                    <Form.Label htmlFor="eventName">इवेंट का नाम<sup>*</sup>:</Form.Label>
                    <Form.Control type='text' id="eventName" name='name' value={eventData.name || ''} onChange={handleInputChange} />
                    {errors.name && <span className="error">{errors.name}</span>}
                </Form.Group>

                <Form.Group className='formGroup'>
                    <Form.Label htmlFor="eventOrganizer">आयोजक<sup>*</sup>:</Form.Label>
                    <Form.Control type='text' id="eventOrganizer" name='organizer' value={eventData.organizer || ''} onChange={handleInputChange} />
                    {errors.organizer && <span className="error">{errors.organizer}</span>}
                </Form.Group>

                <div className='flex gap-4'>
                    <Form.Group className='formGroup'>
                        <Form.Label htmlFor="eventDate">तारीख<sup>*</sup>:</Form.Label>
                        <Form.Control type='date' id="eventDate" name='date' value={eventData.date || ''} onChange={handleInputChange} />
                        {errors.date && <span className="error">{errors.date}</span>}
                    </Form.Group>

                    <Form.Group className='formGroup'>
                        <Form.Label htmlFor="eventTime">समय<sup>*</sup>:</Form.Label>
                        <Form.Control type='time' id="eventTime" name='time' value={eventData.time || ''} onChange={handleInputChange} />
                        {errors.time && <span className="error">{errors.time}</span>}
                    </Form.Group>
                </div>

                <Form.Group className='formGroup'>
                    <Form.Label htmlFor="eventDuration">अवधि:</Form.Label>
                    <Form.Control type='text' id="eventDuration" name='duration' value={eventData.duration || ''} onChange={handleInputChange} placeholder='जैसे 2 घंटे, 30 मिनट' />
                </Form.Group>

                <Form.Group controlId="formEventType" className="mb-3">
                    <Form.Label>इवेंट का प्रकार<sup>*</sup>:</Form.Label>
                    <Form.Select name="eventType" value={eventData.eventType || 'Offline'} onChange={handleInputChange}>
                        <option value="Offline">ऑफलाइन</option>
                        <option value="Online">ऑनलाइन</option>
                        <option value="Hybrid">हाइब्रिड</option>
                    </Form.Select>
                    {errors.eventType && <span className="error">{errors.eventType}</span>}
                </Form.Group>

                {(eventData.eventType === 'Online' || eventData.eventType === 'Hybrid') && (
                    <Form.Group className='formGroup'>
                        <Form.Label htmlFor="zoomLink">ज़ूम लिंक:</Form.Label>
                        <Form.Control type='url' id="zoomLink" name='zoomLink' value={eventData.zoomLink || ''} onChange={handleInputChange} />
                        {errors.zoomLink && <span className="error">{errors.zoomLink}</span>}
                    </Form.Group>
                )}
                <Form.Group className='formGroup'>
                        <Form.Label htmlFor="googleMapUrl">मानचित्र लिंक:</Form.Label>
                        <Form.Control type='url' id="googleMapUrl" name='googleMapUrl' value={eventData.googleMapUrl || ''} onChange={handleInputChange} />
                        {errors.googleMapUrl && <span className="error">{errors.googleMapUrl}</span>}
                    </Form.Group>

                <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>इवेंट बैनर:</Form.Label>
                    {currentBannerUrl && (
                        <div className="mb-2">
                            <img src={currentBannerUrl} alt="Current Banner" style={{ maxWidth: '150px', height: 'auto' }} />
                            <small className="d-block text-muted">वर्तमान बैनर</small>
                        </div>
                    )}
                    <Form.Control type="file" name="banner" accept="image/*" onChange={handleInputChange} />
                    {errors.banner && <span className="error">{errors.banner}</span>}
                </Form.Group>

                <Form.Group className='formGroup'>
                    <Form.Label htmlFor="eventCategory">श्रेणी<sup>*</sup>:</Form.Label>
                    <Form.Select id="eventCategory" name='category' value={eventData.category || ''} onChange={handleInputChange}>
                        <option value="">श्रेणी चुनें</option>
                        <option value="Daily">दैनिक</option>
                        <option value="Weekly">साप्ताहिक</option>
                        <option value="Monthly">मासिक</option>
                        <option value="Yearly">वार्षिक</option>
                    </Form.Select>
                    {errors.category && <span className="error">{errors.category}</span>}
                </Form.Group>

                <Form.Group className='formGroup'>
                    <Form.Label htmlFor="eventPlace">स्थान<sup>*</sup>:</Form.Label>
                    <Form.Control as='textarea' id="eventPlace" name='place' value={eventData.place || ''} onChange={handleInputChange} className='long-content' />
                    {errors.place && <span className="error">{errors.place}</span>}
                </Form.Group>

                <div className="d-flex justify-content-between mt-4">
                    <Button type="submit" disabled={!canSave || isLoading || isDeleting}>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {isLoading ? <Spinner as="span" animation="border" size="sm" /> : saveButtonText}
                    </Button>

                    {/* Delete button only visible in edit mode and for authorized roles */}
                    {(isEditMode && (isManager || isAdmin)) && (
                        <Button variant="danger" onClick={handleDelete} disabled={isDeleting || isLoading}>
                            <FontAwesomeIcon icon={faTrashCan} className="me-2" />
                            {isDeleting ? <Spinner as="span" animation="border" size="sm" /> : 'डिलीट करें'}
                        </Button>
                    )}
                </div>
            </Form>
        </>
    );
};

export default EditEventForm;